import { Alert, AlertTitle, Button, Stack } from '@mui/material';
import { BulkActivationResultItemDTO } from 'probonio-shared-ui/api';
import { downloadFile } from 'probonio-shared-ui/module/api';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BasicDialog, DialogState } from '../../../component/dialog';
import { useBenefitContext } from '../BenefitContext';

interface Props {
  dialogState: DialogState;
  variant: 'deactivate' | 'pause';
  employees: BulkActivationResultItemDTO[];
}

type ResultStatus = 'BLOCKED' | 'ADJUSTMENT';

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function resultDialogKeyFor(variant: Props['variant'], status: ResultStatus): string {
  if (status === 'BLOCKED') {
    return variant === 'pause' ? 'pauseResultDialog' : 'deactivateBlockedDialog';
  }
  return 'deactivateResultDialog';
}

function employeeToCsvRow(emp: BulkActivationResultItemDTO): string[] {
  return [
    emp.targetFirstName ?? '',
    emp.targetLastName ?? '',
    emp.targetMail ?? '',
    emp.targetEmployeeNumber ?? '',
    emp.targetDepartment ?? '',
    emp.targetExternalId ?? '',
    emp.startDate ?? '',
    emp.endDate ?? '',
  ];
}

function downloadEmployeesCsv(filename: string, header: string[], employees: BulkActivationResultItemDTO[]): void {
  const BOM_UTF8 = '\uFEFF'; // UTF-8 BOM ensures Excel on Windows opens umlauts correctly
  const csv = BOM_UTF8 + [header, ...employees.map(employeeToCsvRow)].map(row => row.map(escapeCsvField).join(',')).join('\n');
  downloadFile(filename, csv, 'text/csv;charset=utf-8');
}

export const BulkActivationResultDialog: React.FC<Props> = ({ dialogState, variant, employees }) => {
  const { t } = useTranslation('benefitsModule');
  const { benefit } = useBenefitContext();
  const sharedKey = 'bulkEditDialog.editActivationsResultDialog';

  const groups = useMemo(() => {
    const statuses: ResultStatus[] = ['BLOCKED', 'ADJUSTMENT'];
    return statuses.flatMap(status => {
      const groupEmployees = employees.filter(employee => employee.status === status);
      if (groupEmployees.length === 0) {
        return [];
      }
      const resultDialogKey = resultDialogKeyFor(variant, status);
      const variantKey = `bulkEditDialog.${resultDialogKey}`;
      return [
        {
          status,
          employees: groupEmployees,
          title: t([`${benefit}.${variantKey}.title`, `${variantKey}.title`]),
          message: t([`${benefit}.${variantKey}.message`, `${variantKey}.message`], { count: groupEmployees.length }),
          csvFilename: t([`${benefit}.${variantKey}.csvFilename`, `${variantKey}.csvFilename`]),
          severity: (variant === 'pause' || status === 'BLOCKED' ? 'error' : 'warning') as 'error' | 'warning',
        },
      ];
    });
  }, [benefit, employees, t, variant]);

  const csvHeader = useMemo(
    () => [
      t(`${sharedKey}.csvHeaderFirstName`),
      t(`${sharedKey}.csvHeaderLastName`),
      t(`${sharedKey}.csvHeaderMail`),
      t(`${sharedKey}.csvHeaderEmployeeNumber`),
      t(`${sharedKey}.csvHeaderDepartment`),
      t(`${sharedKey}.csvHeaderExternalId`),
      t(`${sharedKey}.csvHeaderStartDate`),
      t(`${sharedKey}.csvHeaderEndDate`),
    ],
    [sharedKey, t],
  );

  const handleDownload = useCallback(
    (groupEmployees: BulkActivationResultItemDTO[], csvFilename: string): void => {
      downloadEmployeesCsv(csvFilename, csvHeader, groupEmployees);
    },
    [csvHeader],
  );

  if (groups.length === 0) {
    return null;
  }

  const isSingleGroup = groups.length === 1;

  return (
    <BasicDialog
      dialogState={dialogState}
      title={isSingleGroup ? groups[0].title : t(`${sharedKey}.messageTitle`)}
      content={
        <Stack spacing={2}>
          {groups.map(group => (
            <Alert
              key={group.status}
              severity={group.severity}
              action={
                !isSingleGroup ? (
                  <Button color="inherit" size="small" onClick={() => handleDownload(group.employees, group.csvFilename)}>
                    {t(`${sharedKey}.downloadButton`)}
                  </Button>
                ) : undefined
              }
            >
              <AlertTitle>{isSingleGroup ? t(`${sharedKey}.messageTitle`) : group.title}</AlertTitle>
              {group.message}
            </Alert>
          ))}
        </Stack>
      }
      actions={
        <>
          <Button
            type="button"
            onClick={dialogState.handleClose}
            sx={{ marginRight: 2 }}
            variant="text"
            data-test-id="modal-bikeleasing-customer-dialog-button-close"
          >
            {t('common:buttons.close')}
          </Button>
          {isSingleGroup && (
            <Button type="button" variant="contained" onClick={() => handleDownload(groups[0].employees, groups[0].csvFilename)}>
              {t(`${sharedKey}.downloadButton`)}
            </Button>
          )}
        </>
      }
    />
  );
};
